import React from "react";
import Box, { BoxProps } from "@mui/material/Box";
import { EmtTypography } from "./EmtTypography";
import { styled } from "@mui/material/styles";
import Skeleton from "@mui/material/Skeleton";
import { EmtTheme } from "../types";

export type EmtChatMessageProps = BoxProps & {
  /**
   * Type of message
   * red = current user
   * default = target user
   * @default default
   */
  variant?: "default" | "red" | "info" | "app";
  /**
   * Message content
   * @require
   */
  message?: React.ReactNode;
  /**
   * Avatar of the user,
   * if EmtAvatar is used, set size to small for the EmtAvatar component
   * @default undefined
   */
  avatar?: React.ReactNode;
  /**
   * Time of the message
   * @example 9:20
   * @default undefined
   */
  time?: string;
  /**
   * Define if loader is active or not
   * @default false
   */
  loader?: boolean;
  nonce?: string;
};

const DefaultMessageBox = styled(Box)(({ theme }: { theme?: EmtTheme }) => ({
  display: "flex",
  gap: "12px",
  "& .MuiTypography-body1, .MuiSkeleton-text": {
    maxWidth: "298px",
    background: theme?.palette.greyGradient[100],
    borderRadius: "0px 16px 16px 16px",
    padding: "20px",
    paddingBottom: "10px",
  },
  "& .MuiTypography-caption": {
    color: theme?.palette.greyGradient[300],
    display: "block",
    marginTop: "4px",
  },
  "& .MuiSkeleton-root": {
    transform: "none",
  },
  "& img": {
    objectFit: "cover",
  },
  "& a": {
    color: theme?.palette.primary.main,
    textDecoration: "underline",
  },
}));

const RedMessageBox = styled(Box)(({ theme }: { theme?: EmtTheme }) => ({
  display: "flex",
  gap: "12px",
  marginLeft: "auto",
  "& .MuiTypography-body1, .MuiSkeleton-text": {
    maxWidth: "298px",
    background: `linear-gradient(170deg, ${theme?.palette.primary.main} 25.97%, ${theme?.palette.primary[450]} 87.46%);`,
    borderRadius: "16px 0px 16px 16px",
    padding: "20px",
    paddingBottom: "10px",
    color: theme?.palette.common.white,
  },
  "& .MuiSkeleton-text": {
    background: theme?.palette.greyGradient[100],
    color: theme?.palette.greyGradient[450],
  },

  "& .MuiTypography-caption": {
    display: "block",
    marginTop: "4px",
  },
  "& .MuiSkeleton-root": {
    transform: "none",
  },
}));

const AppMessageBox = styled(Box)(({ theme }: { theme?: EmtTheme }) => ({
  display: "flex",
  gap: "12px",
  "& .app-message-box-inner-content": {
    maxWidth: "320px",
    borderRadius: "0px 16px 16px 16px",
    padding: "20px",
    paddingBottom: "10px",
    border: "1px solid",
    borderColor: theme?.palette.greyGradient[100],
  },
}));

export function EmtChatMessage(props: EmtChatMessageProps) {
  const { message, avatar, time, variant = "default", loader, ...rest } = props;

  const ChatMessageComponent =
    variant === "red" ? RedMessageBox : DefaultMessageBox;

  const loaderAvatar = (
    <Skeleton
      variant="rectangular"
      width={32}
      height={32}
      sx={{ borderRadius: "12px" }}
    />
  );

  const loaderBox = (
    <ChatMessageComponent {...rest}>
      {variant === "default" && loaderAvatar}

      <Skeleton variant="text" width="250px" height="100px" />
      {variant === "red" && loaderAvatar}
    </ChatMessageComponent>
  );

  // Split line returns if string
  const finalMessageNode =
    typeof message === "string"
      ? message?.split("\n").map((value, i, array) => {
          return (
            <React.Fragment key={i}>
              {value}
              {i < array.length - 1 && <br />}
            </React.Fragment>
          );
        })
      : message;

  return loader ? (
    loaderBox
  ) : variant === "info" ? (
    <EmtTypography
      variant="caption"
      color="greyGradient.250"
      textAlign="center"
    >
      <>{finalMessageNode}</>
    </EmtTypography>
  ) : variant === "app" ? (
    <AppMessageBox {...rest}>
      <>{avatar}</>
      <div className="app-message-box-inner-content">
        <>
          {finalMessageNode}
          <EmtTypography variant="caption" color="greyGradient.250">
            {time}
          </EmtTypography>
        </>
      </div>
    </AppMessageBox>
  ) : (
    <ChatMessageComponent {...rest}>
      {variant === "default" && <>{avatar}</>}
      <EmtTypography variant="body1" sx={{ wordWrap: "break-word" }}>
        <>{finalMessageNode}</>
        <EmtTypography variant="caption">{time}</EmtTypography>
      </EmtTypography>
      {variant === "red" && <>{avatar}</>}
    </ChatMessageComponent>
  );
}
